import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { prepareReference } from '../lib/reference';
import { initializePage } from '../lib/pageBehavior';
import { MobileContactBar } from '../components/MobileContactBar';
import '../styles/contactNavigation.css';

export default function LandingPage() {
  const host = useRef(null);
  const [content, setContent] = useState(null);
  const [error, setError] = useState(false);
  // React must not rewrite this imperative DOM subtree on hash/router rerenders:
  // doing so detaches root listeners from the forms and resets entered values.
  const markup = useMemo(() => ({ __html: content?.html || '' }), [content]);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);
  // Hash/advertising-query navigation must not dispose a pending lead submission.
  const onAccepted = useCallback(() => navigateRef.current('/thank-you'), []);

  useEffect(() => {
    let disposed = false;
    if (!window.location.hash) window.scrollTo({ top: 0, behavior: 'instant' });
    fetch('/content/jos-immigration.html')
      .then(response => {
        if (!response.ok) throw new Error('Page could not be loaded');
        return response.text();
      })
      .then(source => { if (!disposed) setContent(prepareReference(source)); })
      .catch(() => { if (!disposed) setError(true); });
    document.title = 'Jos Immigration Law';
    return () => { disposed = true; };
  }, []);

  useEffect(() => {
    if (!content || !host.current) return;
    return initializePage(host.current.querySelector('#jil-id-root-1'), onAccepted);
  }, [content, onAccepted]);

  if (error) return <main className="page-status" data-testid="page-error" role="alert">
    <p data-testid="page-error-message">The page could not be loaded.</p>
    <button data-testid="retry-page-button" onClick={() => window.location.reload()}>Try again</button>
  </main>;

  if (!content) return <main className="page-status" data-testid="page-loading" aria-label="Loading" aria-busy="true">
    <img data-testid="loading-logo" src="/brand/jos-approved-reversed-trimmed.png" alt="Jos Immigration Law" />
  </main>;

  return <>
    <style>{content.css}</style>
    <main ref={host} data-testid="immigration-landing-page" dangerouslySetInnerHTML={markup} />
    <MobileContactBar />
  </>;
}